import React from 'react'
import { legacySlotRef } from '@/msgdeskRuntime/legacyBridge/legacySlotRef'
import { selectAccessGrant, selectFavsAllowed, type SubRenderState, type SubRowVariant } from '@/msgdeskRuntime/state'
import { legacySubElementAttrs, legacySubElementId } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { dispatchItemAction } from '@/msgdeskRuntime/itemMovements'
import { dispatchSubsDisclosure } from '@/msgdeskRuntime/subsDisclosure'
import { dispatchSubsNavigation } from '@/msgdeskRuntime/subsNavigation'
import { dispatchSubsSelection } from '@/msgdeskRuntime/subsSelection'
import { useRowSurface } from './RowSurfaceContext'
import type { RowSkin } from './RowTemplateTypes'

// Leaf parts shared by every sub-row variant layout.
// Each part owns its own events and emits the legacy compatibility ids that
// $rjq / legacy code still resolve against. The skeleton emits contract and
// state-marker classes only; visual utilities come from the surface skin.

function stop(event: React.SyntheticEvent) {
  event.preventDefault()
  event.stopPropagation()
}

export type SubIconKind = 'check' | 'fav' | 'qr'

function subIconClassName(kind: SubIconKind, sub: SubRenderState, skin: RowSkin) {
  const classes = ['md-sub-icon', `md-sub-icon--${kind}-control`, skin.icon.base]
  if (kind === 'check') {
    if (sub.flags.isChecked) classes.push('md-sub-icon--checked', skin.icon.checkOn)
    else classes.push('md-sub-icon--flag', skin.icon.checkOff)
  }
  if (kind === 'fav') {
    if (sub.flags.isFavorite) classes.push('md-sub-icon--favorite', skin.icon.favOn)
    else classes.push('md-sub-icon--favorite-grey', skin.icon.favOff)
  }
  if (kind === 'qr') {
    if (sub.flags.isQuickReplyOpen) classes.push('md-sub-icon--qr-on', skin.icon.qrOn)
    else classes.push('md-sub-icon--qr', skin.icon.qrOff)
  }
  return classes.join(' ')
}

export function SubIcon({ sub, kind, sourceMode }: { sub: SubRenderState; kind: SubIconKind; sourceMode?: string }) {
  const { showQuickReplyModal, skin } = useRowSurface()
  const { state } = useSubsStore()

  // FAVS-IDENTITY: no favorites UI for anonymous / show-pin-only viewers on
  // an edit-locked desk (owner state or a logged visitor keeps the star).
  if (kind === 'fav' && !selectFavsAllowed(state)) return null

  // CHECKING = POSSESSION: check/move rides the desk's edit-unlock (legacy
  // !parentIsEditLocked; pinless desks are possessed by everyone). Host-level
  // gate so every row variant obeys one rule — variants must not re-decide.
  // Content visibility is a separate axis: blocked rows keep their checkbox
  // for the possessor (blind moves are legitimate).
  if (kind === 'check' && !selectAccessGrant(state).capabilities.can_check_or_move) return null

  if (kind === 'check') {
    return (
      // SKELETON: checkIcon is the legacy checkbox/flag target for this sub.
      <span
        {...legacySubElementAttrs('checkIcon', sub.elId)}
        className={subIconClassName(kind, sub, skin)}
        title={sub.flags.isChecked ? 'Uncheck' : 'Check'}
        onClick={(event) => {
          stop(event)
          dispatchSubsSelection({
            action: 'set-item',
            section: sub.legacy.data?.is_submsges_section === true ? 'submsges' : 'answers',
            elId: sub.elId,
            checked: !sub.flags.isChecked,
            target: event.currentTarget,
          })
        }}
      />
    )
  }

  if (kind === 'fav') {
    return (
      // SKELETON: favorite is the legacy favorite-toggle target for this sub.
      <span
        {...legacySubElementAttrs('favorite', sub.elId)}
        className={subIconClassName(kind, sub, skin)}
        title={sub.flags.isFavorite ? 'Unfavorite' : 'Favorite'}
        onClick={(event) => {
          stop(event)
          void dispatchItemAction({
            scope: 'item',
            action: sub.flags.isFavorite ? 'remove-favorite' : 'add-favorite',
            elId: sub.elId,
            isFavorite: sub.flags.isFavorite,
            target: event.currentTarget,
            context: sourceMode === 'favs' ? 'favorites' : undefined,
          })
        }}
      />
    )
  }

  return (
    // SKELETON: quickReply is the legacy quick-reply target for this sub.
    <span
      {...legacySubElementAttrs('quickReply', sub.elId)}
      className={subIconClassName(kind, sub, skin)}
      title="Quick reply"
      onClick={(event) => {
        stop(event)
        showQuickReplyModal(sub.elId, true)
      }}
    />
  )
}

export function SubIconStrip({ sub, sourceMode }: { sub: SubRenderState; sourceMode?: string }) {
  return (
    <>
      <SubIcon sub={sub} kind="check" sourceMode={sourceMode} />
      <SubIcon sub={sub} kind="fav" sourceMode={sourceMode} />
      <SubIcon sub={sub} kind="qr" sourceMode={sourceMode} />
    </>
  )
}

/** One canonical direct desk URL for every shared row and folder surface. */
export function subDirectHref(sub: Pick<SubRenderState, 'codeKey' | 'href'>): string {
  const codeKey = String(sub.codeKey || '').trim().replace(/^\/+/, '')
  return codeKey ? `/${codeKey}` : String(sub.href || '').trim()
}

export function SubTitleLink({ sub, label }: { sub: SubRenderState; label?: string }) {
  const { skin } = useRowSurface()
  const { state } = useSubsStore()
  // Some visual variants consume this shared leaf directly instead of the
  // bridge's controls.title slot. Keep the Trash no-URL rule here as the
  // universal last boundary so compact/card/email rows cannot bypass it.
  if (state.renderProjection.mode === 'trash') {
    return <SubSelectionTitle sub={sub} label={label} />
  }
  const href = subDirectHref(sub) || '#'
  return (
    // SKELETON: titleLink virtualizes mcss-sub_link-cssm_<id>. `label` lets a
    // variant show a different link text (legacy compact: namekey for msgs)
    // while keeping this single anchor as the legacy hook.
    <a
      {...legacySubElementAttrs('titleLink', sub.elId)}
      className={`md-sub-title-link md-sub-title-subject ${skin.titleLink}`}
      href={href}
      onClick={(event) => {
        if (href === '#') stop(event)
      }}
    >
      {label ?? sub.title}
    </a>
  )
}

/** Trash keeps the legacy subject-click behavior without exposing a URL. */
export function SubSelectionTitle({ sub, label }: { sub: SubRenderState; label?: string }) {
  const { skin } = useRowSurface()
  const { state } = useSubsStore()
  const canSelect = selectAccessGrant(state).capabilities.can_check_or_move

  if (!canSelect) {
    return (
      <span
        {...legacySubElementAttrs('titleLink', sub.elId)}
        className={`md-sub-title-text md-sub-title-subject ${skin.titleLink}`}
      >
        {label ?? sub.title}
      </span>
    )
  }

  return (
    <span
      {...legacySubElementAttrs('titleLink', sub.elId)}
      className={`md-sub-title-select md-sub-title-subject ${skin.titleLink}`}
      role="button"
      tabIndex={0}
      onClick={(event) => {
        event.stopPropagation()
        dispatchSubsSelection({
          action: 'set-item',
          section: sub.legacy.data?.is_submsges_section === true ? 'submsges' : 'answers',
          elId: sub.elId,
          checked: !sub.flags.isChecked,
          target: event.currentTarget,
        })
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        event.stopPropagation()
        dispatchSubsSelection({
          action: 'set-item',
          section: sub.legacy.data?.is_submsges_section === true ? 'submsges' : 'answers',
          elId: sub.elId,
          checked: !sub.flags.isChecked,
          target: event.currentTarget,
        })
      }}
    >
      {label ?? sub.title}
    </span>
  )
}

export function SubTime({ sub }: { sub: SubRenderState }) {
  const { skin } = useRowSurface()
  const datetime = sub.datetime == null ? '' : String(sub.datetime).trim()
  if (!datetime) return null
  return (
    <span className={`md-sub-time-wrap ${skin.timeWrap}`}>
      {/* SKELETON: time virtualizes time_<id>. */}
      <span {...legacySubElementAttrs('time', sub.elId)} className={`md-sub-time-text ${skin.timeText}`}>
        {datetime}
      </span>
    </span>
  )
}

export function SubHeader({ sub }: { sub: SubRenderState }) {
  return (
    <>
      <SubTitleLink sub={sub} />
      <SubTime sub={sub} />
    </>
  )
}

export function SubText({ sub }: { sub: SubRenderState }) {
  const { skin } = useRowSurface()
  return (
    // Render modeled text only. Structured media is exposed separately through display.media.
    <div {...legacySubElementAttrs('text', sub.elId)} className={`md-sub-text md-sub-text--compact ${skin.text}`}>
      {sub.text}
    </div>
  )
}

// Shared sender email line: the ELEMENT lives in the skeleton's control set;
// each path's variant opts in (shown by the path's will). The author/address
// values arrive precomputed from the bridge's one identity rule — the part
// renders structure only. Look comes from the optional emailFrom skin tokens
// or the consuming variant's wrapper selectors on the structural classes.
export function SubEmailFrom({ author, address }: { author: string; address: string }) {
  const { skin } = useRowSurface()
  const look = skin.emailFrom ?? {}
  if (!author && !address) return null
  return (
    <span className={`md-sub-email-from ${look.root ?? ''}`}>
      {author ? <strong className={`md-sub-email-from__author ${look.author ?? ''}`}>{author}</strong> : null}
      {address ? <span className={`md-sub-email-from__address ${look.address ?? ''}`}>{address}</span> : null}
    </span>
  )
}

export function SubCountLink({
  sub,
  typeId,
  count,
  label,
  isInteractive = true,
}: {
  sub: SubRenderState
  typeId: number
  count: number
  label: string
  isInteractive?: boolean
}) {
  const { skin } = useRowSurface()
  const loadedCount = typeId === 0 ? sub.children.answers.length : sub.children.submsges.length
  const moreCount = count - loadedCount
  const moreLabel = loadedCount > 0 && moreCount > 0 ? `More ${label}(${moreCount})` : ''

  return (
    <React.Fragment>
      <span
        // SKELETON: info virtualizes mcss-sub_info-cssm_<type>_<id>.
        {...legacySubElementAttrs('info', sub.elId, typeId)}
        className={`md-sub-meta-link ${skin.metaLink}`}
        onClick={(event) => {
          if (!isInteractive) return
          stop(event)
          dispatchSubsDisclosure({
            action: 'toggle-children',
            parentElId: sub.elId,
            section: typeId as 0 | 1,
            target: event.currentTarget,
          })
        }}
      >
        {label}({count})
      </span>
      <span
        className={`md-sub-meta-more ${skin.metaMore}`}
        onClick={(event) => {
          if (!isInteractive || !moreLabel) return
          stop(event)
          dispatchSubsNavigation({
            action: 'load-more',
            section: typeId as 0 | 1,
            parentElId: sub.elId,
            target: event.currentTarget,
          })
        }}
      >
        {moreLabel}
      </span>
    </React.Fragment>
  )
}

export function SubMeta({ sub, sourceMode }: { sub: SubRenderState; sourceMode?: string }) {
  const { skin } = useRowSurface()
  const { state } = useSubsStore()
  // Folder pages (favs/drafts/trash) never open or load nested subs — child
  // counts render as plain numbers there (trash always behaved this way).
  const mode = sourceMode || state.renderProjection.mode
  const isInteractive = mode !== 'trash' && mode !== 'favs' && mode !== 'drafts'
  const items = [
    { typeId: 0, count: sub.counts.answers, label: 'answers' },
    { typeId: 1, count: sub.counts.submsges, label: 'submsges' },
  ].filter(item => item.count > 0)

  if (!items.length) return null

  return (
    <div className={`md-sub-meta ${skin.meta}`}>
      {items.map(item => (
        <SubCountLink
          key={item.typeId}
          sub={sub}
          typeId={item.typeId}
          count={item.count}
          label={item.label}
          isInteractive={isInteractive}
        />
      ))}
    </div>
  )
}

// Recursion point: a child feed for one section of one parent sub.
export function SubFeed({ sub, isSubmsges, childVariant }: { sub: SubRenderState; isSubmsges: boolean; childVariant: SubRowVariant }) {
  const { SubRow, skin } = useRowSurface()
  const { state } = useSubsStore()
  const ids = isSubmsges ? sub.children.submsges : sub.children.answers
  const kind = isSubmsges ? 'childrenSubmsges' : 'childrenAnswers'
  const id = legacySubElementId(kind, sub.elId)
  const className = isSubmsges ? `md-sub-feed md-sub-feed--submsges ${skin.feed}` : `md-sub-feed md-sub-feed--answers ${skin.feed}`
  // NESTED-FOLD: React mirror of the legacy tree showed-counter (reconciled
  // by subsDisclosure). Kept MOUNTED while folded — legacy still addresses
  // the ul as a child-list mount target.
  const folded = state.subChildrenHidden[sub.elId]?.[isSubmsges ? 1 : 0] === true
  return (
    // SKELETON: child feed hooks are legacy child-list mount targets.
    <ul
      {...legacySubElementAttrs(kind, sub.elId)}
      className={`${className}${folded ? ' md-sub-feed--folded' : ''}`}
      style={folded ? { display: 'none' } : undefined}
      ref={legacySlotRef(id)}
    >
      {ids.map(childId => (
        <SubRow key={childId} elId={childId} variant={childVariant} />
      ))}
    </ul>
  )
}

export function SubChildren({ sub, childVariant }: { sub: SubRenderState; childVariant: SubRowVariant }) {
  // §0.16: a child submsges feed requires the row to positively be a message
  // (fail-closed) — an answer row can only ever hold answer children.
  const rowCanHaveSubmessages = sub.isMsg === true
  if (!sub.children.answers.length && !(rowCanHaveSubmessages && sub.children.submsges.length)) return null
  return (
    <>
      <SubFeed sub={sub} isSubmsges={false} childVariant={childVariant} />
      {rowCanHaveSubmessages ? <SubFeed sub={sub} isSubmsges childVariant={childVariant} /> : null}
    </>
  )
}
